// apps/web/src/hooks/usePermissions.ts
// WHY: TanStack Query hooks for fetching and evaluating the authenticated user's
// effective permissions. Provides permission-based guards (feature flags, page
// access, effective roles) so frontend components can gate UI without inline
// role-name comparisons.
// Requirements: 3.2, 4.1, 4.2, 4.5, 5.5, 9.4, 10.2

import { useQuery } from '@tanstack/react-query';
import { apiFetch } from './useApiClient';
import type { Role } from '@synapse/shared-types';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** The three permission dimensions for a role. */
export interface PermissionSet {
  pageAccess: string[];
  apiAccess: string[];
  featureFlags: string[];
}

/** Shape returned by GET /api/auth/permissions. */
export interface PermissionsResponse {
  roles: string[];
  effectivePermissions: PermissionSet;
  hierarchy: Record<string, { parents: string[]; description: string }>;
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/**
 * Query hook for fetching the authenticated user's effective permissions and
 * the full role hierarchy. Cached for 5 minutes (staleTime) to balance
 * freshness with performance. All UI gating reads from this cache.
 *
 * Usage:
 * ```tsx
 * const { data: permissions, isLoading } = usePermissions();
 * ```
 */
export function usePermissions() {
  return useQuery<PermissionsResponse>({
    queryKey: ['auth', 'permissions'],
    queryFn: () => apiFetch<PermissionsResponse>('/api/auth/permissions'),
    staleTime: 5 * 60 * 1000,  // WHY: 5-minute stale time per Requirement 9.4
    gcTime: 30 * 60 * 1000,    // WHY: Keep in cache for session duration
    retry: 2,
  });
}

/**
 * Convenience hook for checking a specific feature flag.
 * Returns `true` if the flag is present in the user's effective permissions,
 * `false` otherwise or while loading.
 *
 * WHY: Components call `useHasFeature('trigger_snapshot')` instead of
 * `role === 'coordinator' || role === 'owner'`.
 *
 * Usage:
 * ```tsx
 * const canUpload = useHasFeature('upload_s2score');
 * ```
 */
export function useHasFeature(flag: string): boolean {
  const { data } = usePermissions();
  return data?.effectivePermissions.featureFlags.includes(flag) ?? false;
}

/**
 * Convenience hook for checking page access.
 * Returns `true` if the given path matches any pattern in the user's effective
 * `pageAccess`, `false` otherwise or while loading.
 *
 * WHY: Used by ProtectedRoute and navigation components to gate page access
 * based on permission patterns rather than role names.
 *
 * Usage:
 * ```tsx
 * const canAccessOps = useHasPageAccess('/operations/metrics');
 * ```
 */
export function useHasPageAccess(path: string): boolean {
  const { data } = usePermissions();
  if (!data) return false;
  return matchesAnyPattern(path, data.effectivePermissions.pageAccess);
}

/**
 * Resolve effective roles using the fetched hierarchy via BFS traversal.
 * Collects all inherited roles by walking the parent chain from each user role.
 *
 * WHY: Replaces the hardcoded ROLE_HIERARCHY in ProtectedRoute.tsx with
 * dynamic hierarchy data from the API.
 *
 * Usage:
 * ```tsx
 * const effectiveRoles = useEffectiveRoles(user.roles);
 * const hasAccess = effectiveRoles.has('operations');
 * ```
 */
export function useEffectiveRoles(userRoles: Role[]): Set<Role> {
  const { data } = usePermissions();
  const effective = new Set<Role>(userRoles);

  if (!data?.hierarchy) return effective;

  // WHY: BFS traversal of parent chain to collect all inherited roles
  const queue = [...userRoles];
  while (queue.length > 0) {
    const current = queue.shift()!;
    const entry = data.hierarchy[current];
    if (entry) {
      for (const parent of entry.parents) {
        if (!effective.has(parent as Role)) {
          effective.add(parent as Role);
          queue.push(parent as Role);
        }
      }
    }
  }

  return effective;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Check if a value matches any of the given glob patterns.
 * Supports `*` as a wildcard matching any sequence of characters.
 *
 * WHY: pageAccess patterns use glob syntax (e.g., `/operations*` matches
 * `/operations/metrics`). This avoids requiring exact path matches for
 * hierarchical route structures.
 */
function matchesAnyPattern(value: string, patterns: string[]): boolean {
  for (const pattern of patterns) {
    if (pattern === '*') return true;
    if (pattern === value) return true;
    // WHY: Convert glob pattern to regex — escape dots, replace * with .*
    const regexStr = '^' + pattern.replace(/\./g, '\\.').replace(/\*/g, '.*') + '$';
    try {
      if (new RegExp(regexStr).test(value)) return true;
    } catch {
      // WHY: Skip malformed patterns rather than crashing the UI
      continue;
    }
  }
  return false;
}
